import React from "react";
import {
  FaLayerGroup,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaTrash,
  FaImage,
  FaFilePdf,
  FaMusic,
} from "react-icons/fa";

interface Question {
  id: number;
  questionNumber: number;
  questionType: string;
  questionText: string;
  points: number;
  imageUrl: string | null;
  usesSharedContext: boolean;
}

interface ExamPart {
  id: number;
  displayName: string;
  partTitle: string;
  totalPoints: number;
  questionCount: number;
  contextText: string | null;
  hasContextText: boolean;
  hasContextImage: boolean;
  hasContextDocument: boolean;
  hasContextAudio: boolean;
  hasSharedContext: boolean;
}

interface Props {
  part: ExamPart;
  questions: Question[];
  isExpanded: boolean;
  onToggle: () => void;
  onManageContext: () => void;
  onEditQuestion: (question: Question) => void;
  onDeleteQuestion: (id: number) => void;
}

export const TeilCard: React.FC<Props> = ({
  part,
  questions,
  isExpanded,
  onToggle,
  onManageContext,
  onEditQuestion,
  onDeleteQuestion,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border-l-4 border-[#009fe3]">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={onToggle}
              className="text-[#009fe3] hover:text-cyan-700"
            >
              {isExpanded ? (
                <FaChevronUp size={20} />
              ) : (
                <FaChevronDown size={20} />
              )}
            </button>
            <FaLayerGroup className="text-[#009fe3] text-xl" />
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {part.displayName}
                {part.partTitle && (
                  <span className="text-gray-600 text-sm ml-2">
                    - {part.partTitle}
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {part.questionCount} questions • {part.totalPoints} points
                {part.hasSharedContext && (
                  <span className="ml-2 text-green-600 font-semibold">
                    • 📄 Has context
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onManageContext}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
          >
            <FaEdit size={14} />
            {part.hasSharedContext ? "Edit Context" : "Add Context"}
          </button>
        </div>

        {/* Context Preview */}
        {isExpanded && part.hasSharedContext && (
          <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              Shared Context:
            </p>
            {part.hasContextText && (
              <p className="text-sm text-gray-600 line-clamp-3">
                📝 {part.contextText}
              </p>
            )}
            <div className="flex gap-3 mt-2 text-xs">
              {part.hasContextImage && (
                <span className="text-blue-600 flex items-center gap-1">
                  <FaImage /> Image
                </span>
              )}
              {part.hasContextDocument && (
                <span className="text-red-600 flex items-center gap-1">
                  <FaFilePdf /> PDF
                </span>
              )}
              {part.hasContextAudio && (
                <span className="text-purple-600 flex items-center gap-1">
                  <FaMusic /> Audio
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Questions Table */}
      {isExpanded && (
        <>
          {questions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">No questions yet</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">
                    Question
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-700">
                    Context
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-700">
                    Points
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {questions.map((q) => (
                  <tr key={q.id} className="hover:bg-blue-50">
                    <td className="px-4 py-3">
                      <span className="w-8 h-8 bg-blue-100 text-blue-800 font-bold rounded inline-flex items-center justify-center">
                        {q.questionNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded font-semibold">
                        {q.questionType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 line-clamp-2">
                        {q.questionText}
                      </p>
                      {q.imageUrl && (
                        <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                          <FaImage size={10} /> Has image
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          q.usesSharedContext
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {q.usesSharedContext ? "Shared" : "Own"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="w-10 h-10 bg-green-100 text-green-800 font-bold rounded inline-flex items-center justify-center">
                        {q.points}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => onEditQuestion(q)}
                          className="px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 text-sm font-semibold flex items-center gap-1"
                        >
                          <FaEdit size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => onDeleteQuestion(q.id)}
                          className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-semibold flex items-center gap-1"
                        >
                          <FaTrash size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};
